import type { Party } from '@erp/shared';

import { api } from './api.js';

/** What GET /parties will answer in ONE request — the server's hard cap. */
const PAGE = 500;

/**
 * Every party matching `params` — not just the first page.
 *
 * GET /parties answers at most 500 rows, and only 200 when the caller sends no
 * `limit` at all. A real customer book runs to thousands, so a single request
 * quietly cut every list, picker and name-lookup off at the 200th (or 500th)
 * name: the customers past it did not exist as far as the screen was concerned,
 * which reads as "the customer is missing" rather than as a paging limit. This
 * pages to the end.
 *
 * `onPage` is handed the rows gathered so far after each page, so a table can
 * paint the first 500 at once and let the tail stream in; returning `false`
 * from it abandons the rest of the sweep (the caller moved on).
 *
 * Generic in the row type: several callers declare a narrow shape (id/code/
 * displayName) rather than dragging in the full `Party`.
 */
export async function fetchAllParties<T = Party>(
  params: Record<string, string | number | undefined> = {},
  onPage?: (soFar: T[]) => boolean | void,
): Promise<T[]> {
  const all: T[] = [];
  // `total` (or a short page) is what ends this loop; the bound is only a fuse
  // against a server that would otherwise answer full pages forever.
  for (let offset = 0; offset < 100_000; offset += PAGE) {
    const r = await api().get<{ items: T[]; total?: number }>('/parties', {
      params: { ...params, limit: PAGE, offset },
    });
    all.push(...r.data.items);
    // A fresh array each time — handing back the same reference would leave a
    // React caller's setState seeing no change and never re-rendering.
    if (onPage?.([...all]) === false) return all;
    if (r.data.items.length < PAGE || all.length >= (r.data.total ?? all.length)) break;
  }
  return all;
}
